import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Image,
    Video,
    Calendar as CalendarIcon,
    Clock,
    Hash,
    MapPin,
    Send,
    Save,
    X,
    Plus,
    Loader2,
    Upload
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const PLATFORMS = [
    { id: 'instagram', name: 'Instagram', emoji: '📸' },
    { id: 'facebook', name: 'Facebook', emoji: '👤' },
    { id: 'twitter', name: 'Twitter/X', emoji: '🐦' },
    { id: 'linkedin', name: 'LinkedIn', emoji: '💼' },
    { id: 'tiktok', name: 'TikTok', emoji: '🎵' },
    { id: 'pinterest', name: 'Pinterest', emoji: '📌' },
    { id: 'youtube', name: 'YouTube Shorts', emoji: '🎬' },
];

export default function Compose() {
    const { api } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const editId = searchParams.get('edit');
    const mediaParam = searchParams.get('media');

    const [caption, setCaption] = useState('');
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [scheduledDate, setScheduledDate] = useState(null);
    const [scheduledTime, setScheduledTime] = useState('12:00');
    const [firstComment, setFirstComment] = useState('');
    const [location, setLocation] = useState('');
    const [selectedMedia, setSelectedMedia] = useState([]);
    const [selectedHashtagGroups, setSelectedHashtagGroups] = useState([]);
    const [instagramPostType, setInstagramPostType] = useState('photo');
    
    const [accounts, setAccounts] = useState([]);
    const [media, setMedia] = useState([]);
    const [hashtagGroups, setHashtagGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [accountsRes, mediaRes, hashtagsRes] = await Promise.all([
                api.get('/social-accounts'),
                api.get('/media'),
                api.get('/hashtag-groups')
            ]);
            setAccounts(accountsRes.data);
            setMedia(mediaRes.data);
            setHashtagGroups(hashtagsRes.data);

            if (editId) {
                const postRes = await api.get(`/posts/${editId}`);
                const post = postRes.data;
                setCaption(post.caption || '');
                setSelectedPlatforms(post.platforms || []);
                setSelectedAccounts(post.social_account_ids || []);
                setSelectedMedia(post.media_ids || []);
                setSelectedHashtagGroups(post.hashtag_group_ids || []);
                setInstagramPostType(post.instagram_post_type || 'photo');
                setFirstComment(post.first_comment || '');
                setLocation(post.location || '');
                if (post.scheduled_at) {
                    const date = new Date(post.scheduled_at);
                    setScheduledDate(date);
                    setScheduledTime(format(date, 'HH:mm'));
                }
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [api, editId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Pre-select media items passed via ?media=id1,id2,id3 (e.g. from Media Library)
    useEffect(() => {
        if (!editId && mediaParam) {
            const ids = mediaParam.split(',').filter(Boolean);
            if (ids.length > 0) setSelectedMedia(prev => Array.from(new Set([...prev, ...ids])));
        }
    }, [editId, mediaParam]);

    const handlePlatformToggle = (platformId) => {
        const wasSelected = selectedPlatforms.includes(platformId);
        setSelectedPlatforms(prev =>
            wasSelected ? prev.filter(p => p !== platformId) : [...prev, platformId]
        );
        const platformAccounts = accounts.filter(a => a.platform === platformId);
        const platformAccountIds = platformAccounts.map(a => a.id);
        if (wasSelected) {
            setSelectedAccounts(prev => prev.filter(id => !platformAccountIds.includes(id)));
        } else if (platformAccounts.length === 1) {
            setSelectedAccounts(prev => prev.includes(platformAccountIds[0]) ? prev : [...prev, platformAccountIds[0]]);
        }
    };

    const handleAccountToggle = (accountId) => {
        setSelectedAccounts(prev =>
            prev.includes(accountId)
                ? prev.filter(a => a !== accountId)
                : [...prev, accountId]
        );
    };

    const handleMediaToggle = (mediaId) => {
        setSelectedMedia(prev =>
            prev.includes(mediaId)
                ? prev.filter(m => m !== mediaId)
                : [...prev, mediaId]
        );
    };

    const handleHashtagGroupToggle = (groupId) => {
        setSelectedHashtagGroups(prev =>
            prev.includes(groupId)
                ? prev.filter(g => g !== groupId)
                : [...prev, groupId]
        );
    };

    const handleInlineUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        const newIds = [];
        const newDocs = [];
        for (const file of Array.from(files)) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const res = await api.post('/media/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (res.data?.id) {
                    newIds.push(res.data.id);
                    newDocs.push(res.data);
                }
            } catch (err) {
                toast.error(`Failed to upload ${file.name}`);
            }
        }

        if (newDocs.length > 0) {
            setMedia(prev => [...newDocs, ...prev]);
            setSelectedMedia(prev => [...prev, ...newIds]);
            toast.success(`Uploaded ${newDocs.length} file(s)`);
        }

        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const getScheduledDateTime = () => {
        if (!scheduledDate) return null;
        const [hours, minutes] = scheduledTime.split(':').map(Number);
        const date = new Date(scheduledDate);
        date.setHours(hours, minutes, 0, 0);
        return date;
    };

    const handleSave = async (status = 'draft') => {
        if (!caption.trim()) {
            toast.error('Please enter a caption');
            return;
        }

        if (selectedPlatforms.length === 0) {
            toast.error('Please select at least one platform');
            return;
        }

        setSaving(true);
        try {
            const postData = {
                caption,
                platforms: selectedPlatforms,
                social_account_ids: selectedAccounts,
                media_ids: selectedMedia,
                hashtag_group_ids: selectedHashtagGroups,
                first_comment: firstComment || null,
                location: location || null,
                scheduled_at: status === 'scheduled' ? getScheduledDateTime()?.toISOString() : null,
                status,
                instagram_post_type: selectedPlatforms.includes('instagram') ? instagramPostType : null,
            };

            if (editId) {
                await api.put(`/posts/${editId}`, postData);
                toast.success('Post updated successfully');
            } else {
                await api.post('/posts', postData);
                toast.success(status === 'scheduled' ? 'Post scheduled!' : 'Draft saved!');
            }
            navigate('/calendar');
        } catch (error) {
            console.error('Failed to save post:', error);
            toast.error('Failed to save post');
        } finally {
            setSaving(false);
        }
    };

    const handlePublishNow = async () => {
        if (!caption.trim()) {
            toast.error('Please enter a caption');
            return;
        }
        if (selectedPlatforms.length === 0) {
            toast.error('Please select at least one platform');
            return;
        }
        if (selectedAccounts.length === 0) {
            toast.error('Please select at least one account to publish to');
            return;
        }
        if (selectedMedia.length === 0) {
            toast.error('Please attach at least one image or video');
            return;
        }

        setPublishing(true);
        try {
            // Create the post as a draft first
            const postData = {
                caption,
                platforms: selectedPlatforms,
                social_account_ids: selectedAccounts,
                media_ids: selectedMedia,
                hashtag_group_ids: selectedHashtagGroups,
                first_comment: firstComment || null,
                location: location || null,
                scheduled_at: null,
                status: 'draft',
                instagram_post_type: selectedPlatforms.includes('instagram') ? instagramPostType : null,
            };

            let postId;
            if (editId) {
                await api.put(`/posts/${editId}`, postData);
                postId = editId;
            } else {
                const res = await api.post('/posts', postData);
                postId = res.data.id;
            }

            // Trigger immediate publish
            await api.post(`/posts/${postId}/publish`);
            toast.success('Publishing now! This may take a moment.');
            navigate('/calendar');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to publish post';
            toast.error(message);
        } finally {
            setPublishing(false);
        }
    };

    const getHashtagsString = () => {
        const groups = hashtagGroups.filter(g => selectedHashtagGroups.includes(g.id));
        return groups.flatMap(g => g.hashtags).join(' ');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="compose-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">
                        {editId ? 'Edit Post' : 'Create Post'}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Compose and schedule your social media content
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => handleSave('draft')}
                        disabled={saving || publishing}
                        data-testid="save-draft-btn"
                    >
                        <Save className="mr-2 h-4 w-4" />
                        Save Draft
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => handleSave('scheduled')}
                        disabled={saving || publishing || !scheduledDate}
                        data-testid="schedule-btn"
                    >
                        {saving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <CalendarIcon className="mr-2 h-4 w-4" />
                        )}
                        Schedule
                    </Button>
                    <Button
                        className="gradient-bg"
                        onClick={handlePublishNow}
                        disabled={saving || publishing}
                        data-testid="publish-now-btn"
                    >
                        {publishing ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="mr-2 h-4 w-4" />
                        )}
                        Publish Now
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Content</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="caption">Caption</Label>
                                <Textarea
                                    id="caption"
                                    placeholder="Write your post caption..."
                                    value={caption}
                                    onChange={(e) => setCaption(e.target.value)}
                                    className="min-h-[150px] resize-none"
                                    data-testid="caption-input"
                                />
                                <div className="text-sm text-muted-foreground text-right">
                                    {caption.length} characters
                                </div>
                            </div>

                            {selectedPlatforms.includes('instagram') && (
                                <div className="space-y-2 p-3 rounded-lg border border-primary/20 bg-primary/5">
                                    <Label className="text-sm flex items-center gap-2">
                                        <span className="text-base">📸</span>
                                        Instagram post type
                                    </Label>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={instagramPostType === 'photo' ? 'default' : 'outline'}
                                            onClick={() => setInstagramPostType('photo')}
                                            className="flex-1"
                                            data-testid="ig-type-photo"
                                        >
                                            Photo / Carousel
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={instagramPostType === 'reel' ? 'default' : 'outline'}
                                            onClick={() => setInstagramPostType('reel')}
                                            className="flex-1"
                                            data-testid="ig-type-reel"
                                        >
                                            Reel
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {instagramPostType === 'reel'
                                            ? 'Requires exactly one video. Will publish as a Reel.'
                                            : 'Single image, carousel, or feed video.'}
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Media</Label>
                                    <button
                                        type="button"
                                        className="text-xs text-muted-foreground hover:text-primary underline"
                                        onClick={() => navigate('/media')}
                                    >
                                        Browse library
                                    </button>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                                    multiple
                                    className="hidden"
                                    onChange={handleInlineUpload}
                                />
                                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                    {selectedMedia
                                        .map(id => media.find(m => m.id === id))
                                        .filter(Boolean)
                                        .map(item => (
                                        <div
                                            key={item.id}
                                            className="aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-primary ring-2 ring-primary/20 transition-all relative group"
                                            onClick={() => handleMediaToggle(item.id)}
                                            data-testid={`media-item-${item.id}`}
                                        >
                                            {item.file_type === 'image' ? (
                                                <img
                                                    src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                                                    alt={item.original_filename}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <video
                                                    src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                                                    className="w-full h-full object-cover"
                                                    muted
                                                    preload="metadata"
                                                />
                                            )}
                                            {item.file_type === 'video' && (
                                                <div className="absolute bottom-1 right-1 bg-black/60 rounded p-0.5">
                                                    <Video className="h-3 w-3 text-white" />
                                                </div>
                                            )}
                                            {selectedMedia.includes(item.id) && (
                                                <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                                                    {selectedMedia.indexOf(item.id) + 1}
                                                </div>
                                            )}
                                            {item.r2_url && (
                                                <a
                                                    href={item.r2_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute bottom-1 left-1 px-1 py-0.5 rounded bg-emerald-600/80 text-white text-[9px] font-medium leading-none"
                                                    title="Staged on R2 — click to open"
                                                >
                                                    R2
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                    <Button
                                        variant="outline"
                                        className="aspect-square flex flex-col gap-1 items-center justify-center"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        data-testid="inline-upload-btn"
                                    >
                                        {uploading ? (
                                            <Loader2 className="h-6 w-6 animate-spin" />
                                        ) : (
                                            <>
                                                <Upload className="h-5 w-5" />
                                                <span className="text-[10px] leading-none">Upload</span>
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="firstComment">First Comment (Optional)</Label>
                                <Textarea
                                    id="firstComment"
                                    placeholder="Add a first comment with hashtags..."
                                    value={firstComment}
                                    onChange={(e) => setFirstComment(e.target.value)}
                                    className="min-h-[80px] resize-none"
                                    data-testid="first-comment-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="location">Location (Optional)</Label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="location"
                                        placeholder="Add location..."
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                        className="pl-10"
                                        data-testid="location-input"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Hash className="h-5 w-5" />
                                Hashtag Groups
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {hashtagGroups.length > 0 ? (
                                <div className="space-y-3">
                                    {hashtagGroups.map(group => (
                                        <div
                                            key={group.id}
                                            className={cn(
                                                "p-3 rounded-lg border cursor-pointer transition-colors",
                                                selectedHashtagGroups.includes(group.id)
                                                    ? "border-primary bg-primary/5"
                                                    : "border-border hover:border-muted-foreground/50"
                                            )}
                                            onClick={() => handleHashtagGroupToggle(group.id)}
                                            data-testid={`hashtag-group-${group.id}`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium">{group.name}</span>
                                                <Checkbox
                                                    checked={selectedHashtagGroups.includes(group.id)}
                                                    onChange={() => {}}
                                                />
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {group.hashtags.slice(0, 5).map(tag => (
                                                    <Badge key={tag} variant="secondary" className="text-xs">
                                                        {tag}
                                                    </Badge>
                                                ))}
                                                {group.hashtags.length > 5 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        +{group.hashtags.length - 5}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-muted-foreground">
                                    <Hash className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p>No hashtag groups yet</p>
                                    <Button
                                        variant="link"
                                        className="mt-2"
                                        onClick={() => navigate('/hashtags')}
                                    >
                                        Create one
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Platforms</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {PLATFORMS.map(platform => {
                                const platformAccounts = accounts.filter(a => a.platform === platform.id);
                                const isSelected = selectedPlatforms.includes(platform.id);

                                return (
                                    <div key={platform.id} className="space-y-2">
                                        <div
                                            className={cn(
                                                "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                                                isSelected
                                                    ? "border-primary bg-primary/5"
                                                    : "border-border hover:border-muted-foreground/50"
                                            )}
                                            onClick={() => handlePlatformToggle(platform.id)}
                                            data-testid={`platform-${platform.id}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-xl">{platform.emoji}</span>
                                                <span className="font-medium">{platform.name}</span>
                                            </div>
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => {}}
                                            />
                                        </div>
                                        {isSelected && platformAccounts.length === 0 && (
                                            <div className="ml-4 text-xs text-destructive">
                                                No connected {platform.name} accounts. Connect one in Accounts.
                                            </div>
                                        )}
                                        {isSelected && platformAccounts.length > 0 && !platformAccounts.some(a => selectedAccounts.includes(a.id)) && (
                                            <div className="ml-4 text-xs text-amber-500 font-medium">
                                                ⚠ Pick an account below to publish to {platform.name}
                                            </div>
                                        )}
                                        {isSelected && platformAccounts.length > 0 && (
                                            <div className="ml-4 space-y-1">
                                                {platformAccounts.map(account => (
                                                    <div
                                                        key={account.id}
                                                        className={cn(
                                                            "flex items-center justify-between p-2 rounded text-sm cursor-pointer",
                                                            selectedAccounts.includes(account.id)
                                                                ? "bg-muted"
                                                                : "hover:bg-muted/50"
                                                        )}
                                                        onClick={() => handleAccountToggle(account.id)}
                                                    >
                                                        <span>{account.account_name}</span>
                                                        <Checkbox
                                                            checked={selectedAccounts.includes(account.id)}
                                                            onChange={() => {}}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CalendarIcon className="h-5 w-5" />
                                Schedule
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !scheduledDate && "text-muted-foreground"
                                        )}
                                        data-testid="schedule-date-btn"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {scheduledDate ? format(scheduledDate, 'PPP') : 'Pick a date'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={scheduledDate}
                                        onSelect={setScheduledDate}
                                        disabled={(date) => { const t = new Date(); t.setHours(0,0,0,0); return date < t; }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>

                            <div className="space-y-2">
                                <Label htmlFor="time">Time</Label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="time"
                                        type="time"
                                        value={scheduledTime}
                                        onChange={(e) => setScheduledTime(e.target.value)}
                                        className="pl-10"
                                        data-testid="schedule-time-input"
                                    />
                                </div>
                            </div>

                            {scheduledDate && (
                                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                                    <div className="text-sm text-muted-foreground">Scheduled for</div>
                                    <div className="font-medium text-primary">
                                        {format(getScheduledDateTime(), 'PPP p')}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
